import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import ImageCropUtils from '../utils/imageCrop.utils';

// minimum crop size, in pixels, as displayed in the dialog (not the source image's
// native resolution) - keeps users from dragging out a crop that's too small to be useful
const MIN_CROP_SIZE = 40;

const isCropLargeEnough = (crop) =>
  !!crop && crop.width >= MIN_CROP_SIZE && crop.height >= MIN_CROP_SIZE;

const ImageCropDialog = ({
  open,
  imageSrc,
  fileName,
  onCancel,
  onCropComplete,
}) => {
  const imageRef = useRef(null);
  const [crop, setCrop] = useState();
  const [pixelCrop, setPixelCrop] = useState();
  const [submitting, setSubmitting] = useState(false);

  function onImageLoad(event) {
    const { width, height } = event.currentTarget;
    setCrop({
      unit: '%',
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    });
    setPixelCrop({ x: 0, y: 0, width, height });
  }

  const handleConfirm = async () => {
    if (!isCropLargeEnough(pixelCrop) || !imageRef.current) {
      return;
    }
    setSubmitting(true);
    const croppedFile = await ImageCropUtils.getCroppedFile(
      imageRef.current,
      pixelCrop,
      fileName
    );
    setSubmitting(false);
    onCropComplete(croppedFile);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Crop image</DialogTitle>
      <DialogContent>
        {imageSrc && (
          <ReactCrop
            crop={crop}
            minWidth={MIN_CROP_SIZE}
            minHeight={MIN_CROP_SIZE}
            onChange={(newPixelCrop, newPercentCrop) => {
              setCrop(newPercentCrop);
              setPixelCrop(newPixelCrop);
            }}
          >
            <img
              ref={imageRef}
              src={imageSrc}
              alt="Crop preview"
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
        {!isCropLargeEnough(pixelCrop) && (
          <Typography
            variant="body2"
            color="error"
            style={{ marginTop: '10px' }}
          >
            Crop area must be at least {MIN_CROP_SIZE}x{MIN_CROP_SIZE} pixels.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isCropLargeEnough(pixelCrop) || submitting}
        >
          Use this image
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;

ImageCropDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string,
  fileName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onCropComplete: PropTypes.func.isRequired,
};

ImageCropDialog.defaultProps = {
  imageSrc: null,
  fileName: 'cropped-image',
};
